/**
 * The deployment descriptor, loaded once at boot before the router mounts, so
 * nothing signs against an absent LOP address. A failed load leaves the cache
 * empty and every getter degrades (addresses `null`: callers refuse to sign).
 */

import { api, type ConfigResponse, type ConfigTokenResponse } from "./api";

export type TokenKind = "erc20" | "fiat";

export interface RuntimeToken {
  symbol: string;
  name: string | null;
  /** Base-unit decimals per the deployed contract; null when unknown. */
  decimals: number | null;
  /** Deployed address, or null when the deployment has not provisioned it. */
  address: string | null;
  kind: TokenKind;
}

export interface RuntimeConfig {
  /** A positive safe integer, else null. */
  chainId: number | null;
  network: string | null;
  /** `commit`: the intent-protocol source the addresses were deployed from, when known. */
  contracts: { lop: string | null; settlement: string | null; commit?: string | null };
  tokens: RuntimeToken[];
  markets?: { tokens: RuntimeToken[]; directions: { sellToken: string; buyToken: string }[] };
  explorerBaseUrl: string | null;
  faucet: { enabled: boolean };
  auth: { domain: string | null; uri: string | null };
  emailGate: { enabled: boolean; supabaseUrl: string | null; supabaseAnonKey: string | null };
  /** `projectId` null: the sign-in screen offers no WalletConnect. */
  walletConnect: { projectId: string | null };
  /**
   * Largest ticket in whole dollars, 0 when uncapped. Checked before the wallet
   * prompt: a signed order the venue refuses is worse than a disabled button.
   */
  maxTicketUsd: number;
}

/** An address is only usable if it looks like one — anything else is "unknown". */
function readAddress(value: unknown): string | null {
  return typeof value === "string" && /^0x[0-9a-fA-F]{40}$/.test(value)
    ? value.toLowerCase()
    : null;
}

function readToken(raw: ConfigTokenResponse): RuntimeToken | null {
  if (!raw || typeof raw !== "object") return null;
  if (typeof raw.symbol !== "string" || raw.symbol === "") return null;
  return {
    symbol: raw.symbol,
    name: typeof raw.name === "string" ? raw.name : null,
    decimals:
      typeof raw.decimals === "number" && Number.isInteger(raw.decimals) && raw.decimals >= 0 && raw.decimals <= 255
        ? raw.decimals
        : null,
    address: readAddress(raw.address),
    kind: raw.kind === "fiat" ? "fiat" : "erc20",
  };
}

function normalizeMarkets(raw: ConfigResponse['markets']): NonNullable<RuntimeConfig['markets']> {
  const tokens = (Array.isArray(raw?.tokens) ? raw.tokens : []).map(readToken)
    .filter((t): t is RuntimeToken => t !== null && t.address !== null && t.decimals !== null && t.kind === "erc20");
  const known = new Set(tokens.map((t) => t.symbol));
  const directions = (Array.isArray(raw?.directions) ? raw.directions : [])
    .filter((d) => d && typeof d.sellToken === "string" && typeof d.buyToken === "string" &&
      d.sellToken !== d.buyToken && known.has(d.sellToken) && known.has(d.buyToken));
  const used = new Set(directions.flatMap((d) => [d.sellToken, d.buyToken]));
  return { tokens: tokens.filter((t) => used.has(t.symbol)), directions };
}

/** Folds the optional-everything wire payload into a complete config object. */
export function normalizeConfig(raw: ConfigResponse): RuntimeConfig {
  const explorer =
    typeof raw.explorerBaseUrl === "string" && raw.explorerBaseUrl !== ""
      ? raw.explorerBaseUrl.replace(/\/+$/, "")
      : null;
  return {
    chainId:
      typeof raw.chainId === "number" && Number.isSafeInteger(raw.chainId) && raw.chainId > 0
        ? raw.chainId
        : null,
    network: typeof raw.network === "string" && raw.network !== "" ? raw.network : null,
    contracts: {
      lop: readAddress(raw.contracts?.lop),
      settlement: readAddress(raw.contracts?.settlement),
    },
    tokens: [...(Array.isArray(raw.tokens) ? raw.tokens : []), ...(Array.isArray(raw.bookTokens) ? raw.bookTokens : [])]
      .map(readToken).filter((t): t is RuntimeToken => t !== null),
    ...(raw.markets !== undefined ? { markets: normalizeMarkets(raw.markets) } : {}),
    explorerBaseUrl: explorer,
    maxTicketUsd:
      typeof raw.limits?.maxTicketUsd === "number" && raw.limits.maxTicketUsd > 0
        ? raw.limits.maxTicketUsd
        : 0,
    faucet: { enabled: raw.faucet?.enabled === true },
    auth: {
      domain: typeof raw.auth?.domain === "string" ? raw.auth.domain : null,
      uri: typeof raw.auth?.uri === "string" ? raw.auth.uri : null,
    },
    emailGate: {
      enabled: raw.emailGate?.enabled === true,
      supabaseUrl:
        typeof raw.emailGate?.supabaseUrl === "string" && raw.emailGate.supabaseUrl !== ""
          ? raw.emailGate.supabaseUrl.replace(/\/+$/, "")
          : null,
      supabaseAnonKey:
        typeof raw.emailGate?.supabaseAnonKey === "string" && raw.emailGate.supabaseAnonKey !== ""
          ? raw.emailGate.supabaseAnonKey
          : null,
    },
    walletConnect: {
      projectId:
        typeof raw.walletConnect?.projectId === "string" && raw.walletConnect.projectId !== ""
          ? raw.walletConnect.projectId
          : null,
    },
  };
}

const listeners = new Set<() => void>();
export function subscribeRuntimeConfig(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
function publish(): void { for (const listener of listeners) listener(); }
let cache: RuntimeConfig | null = null;
let inflight: Promise<RuntimeConfig | null> | null = null;

/**
 * Never rejects: a missing descriptor is a degraded, sign-nothing state. Repeat
 * calls return the cache or join the request in flight.
 */
export function loadRuntimeConfig(refresh = false): Promise<RuntimeConfig | null> {
  if (!refresh && cache !== null) return Promise.resolve(cache);
  if (inflight !== null) return inflight;
  inflight = api
    .config()
    .then((raw) => {
      const next = normalizeConfig(raw);
      if (refresh && cache && (next.chainId !== cache.chainId || next.contracts.lop !== cache.contracts.lop ||
          next.contracts.settlement !== cache.contracts.settlement)) {
        cache = { ...cache, markets: { tokens: [], directions: [] } };
      } else cache = cache?.markets && !next.markets ? { ...next, markets: { tokens: [], directions: [] } } : next;
      publish();
      return cache;
    })
    .catch(() => {
      // The api layer already reported what it should: degrade, never throw.
      if (refresh && cache?.markets) {
        cache = { ...cache, markets: { tokens: [], directions: [] } };
        publish();
      }
      return null;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Cached config, or null when it has not loaded (or failed to load). */
export function getRuntimeConfig(): RuntimeConfig | null {
  return cache;
}

/** Replaces the cache; for tests. */
export function setRuntimeConfig(config: RuntimeConfig | null): void {
  cache = config;
  inflight = null;
  publish();
}

/** Chain id the deployment runs on; null until the descriptor names one. */
export function getChainId(): number | null {
  return cache?.chainId ?? null;
}

/** Network name ("base"), used for the sidebar badge. */
export function getNetwork(): string | null {
  return cache?.network ?? null;
}

/** "base" -> "Base". */
export function getNetworkLabel(): string | null {
  const network = getNetwork();
  return network === null ? null : network.charAt(0).toUpperCase() + network.slice(1);
}

/** EIP-712 verifyingContract. `null`: not known yet, so callers refuse to sign. */
export function getLopAddress(): string | null {
  return cache?.contracts.lop ?? null;
}

export function getSettlementAddress(): string | null {
  return cache?.contracts.settlement ?? null;
}

/** Token descriptors from the deployment (empty before config lands). */
export function getTokens(): RuntimeToken[] {
  return cache?.tokens ?? [];
}

export function getTokenConfig(symbol: string): RuntimeToken | null {
  return cache?.markets?.tokens.find((t) => t.symbol === symbol) ?? cache?.tokens.find((t) => t.symbol === symbol) ?? null;
}

/** Null when the descriptor names no explorer: callers draw no link. */
export function getExplorerTxUrl(txHash: string): string | null {
  const base = cache?.explorerBaseUrl ?? null;
  return base === null ? null : `${base}/tx/${txHash}`;
}

export function getExplorerAddressUrl(address: string): string | null {
  const base = cache?.explorerBaseUrl ?? null;
  return base === null ? null : `${base}/address/${address}`;
}

/** False until the descriptor says otherwise, so the entry never flashes into a mainnet menu. */
export function isFaucetEnabled(): boolean {
  return cache?.faucet.enabled ?? false;
}

/**
 * 0 means uncapped, also when unknown: an unknown limit must not block trades the
 * venue accepts.
 */
export function getMaxTicketUsd(): number {
  return cache?.maxTicketUsd ?? 0;
}

/**
 * Disabled until the descriptor lands: the backend enforces the gate, so no order
 * leaks meanwhile.
 */
export function getEmailGate(): {
  enabled: boolean;
  supabaseUrl: string | null;
  supabaseAnonKey: string | null;
} {
  return cache?.emailGate ?? { enabled: false, supabaseUrl: null, supabaseAnonKey: null };
}

/**
 * Null with no project id or no chain id (a session is proposed for that chain
 * alone): the option is then not offered, rather than hang on the relay's 401.
 */
export function getWalletConnectProjectId(): string | null {
  if (cache === null || cache.chainId === null) return null;
  return cache.walletConnect.projectId;
}

/** SIWE domain/uri the gateway expects; null falls back to window.location. */
export function getAuthDomain(): string | null {
  return cache?.auth.domain ?? null;
}

export function getAuthUri(): string | null {
  return cache?.auth.uri ?? null;
}

export function marketAvailable(sell: string, buy: string): boolean {
  const markets = cache?.markets;
  return markets === undefined || markets.directions.some((d) => d.sellToken === sell && d.buyToken === buy);
}

export function marketIdentity(sell: string, buy: string): string {
  return JSON.stringify([cache?.chainId, cache?.contracts.lop,
    ...[sell, buy].map((symbol) => { const token = getTokenConfig(symbol); return [symbol, token?.address, token?.decimals]; })]);
}
